import { julianDay, geocentricLongitude, isRetrograde, lunarNode, lunarNodeTrue, lilithLongitude, ascMC, normalize } from './ephemeris';
import { getCusps, houseOf, HouseSystem } from './houses';
import { aspectBetweenPoints, ChartAspect, signOf } from './aspects';
import { ALL_POINTS, CORE_POINTS, SIGN_ELEMENT, SIGN_MODALITY } from '../data/constants';
import { computeChartPatterns, ChartPatterns } from './chartPatterns';

export interface BirthData {
  name?: string;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  tzOffset: number;
  lat: number;
  lon: number;
  placeName?: string;
  imported?: boolean;
  /** When true, the user did not know their birth time. Houses, ASC/MC are suppressed. */
  unknownTime?: boolean;
  /** House system used to cast the chart. Defaults to Placidus. */
  houseSystem?: HouseSystem;
  /** Lunar node algorithm. Defaults to the True Node (what most tools display). */
  nodeType?: 'mean' | 'true';
}

export interface Placement {
  longitude: number;
  house: number | null;
  retrograde: boolean;
  /** Set on imported charts where direct/retrograde motion was not supplied. */
  retrogradeUnknown?: boolean;
}

export interface Chart {
  JD: number | null;
  planets: Record<string, number>;
  retrogrades: Record<string, boolean>;
  placements: Record<string, Placement>;
  cusps: number[];
  asc: number;
  mc: number;
  aspects: ChartAspect[];
  birthData: BirthData;
  /** Engine version — bump when scoring/house logic changes so stale caches recompute. */
  engineVersion?: number;
  /** Detected chart-level patterns (stelliums, balance, aspect patterns, shape). */
  patterns?: ChartPatterns;
  /** Set when birth time is unknown — house/ASC/MC fields are not meaningful. */
  unknownTime?: boolean;
  /** Algorithm choice for the lunar node ("mean" by default; "true" if requested). */
  nodeType?: 'mean' | 'true';
  /** House system used to compute this chart. */
  houseSystem?: HouseSystem;
  /** Set when the MC was not supplied and had to be derived from the Ascendant. */
  mcDerived?: boolean;
}

/** Bump whenever chart math (houses, aspects, placements) changes. */
export const CHART_ENGINE_VERSION = 7;

/**
 * Part of Fortune (Lot of Fortune).
 * Day chart (Sun above the horizon): Asc + Moon − Sun
 * Night chart (Sun below the horizon): Asc + Sun − Moon
 */
export function partOfFortune(asc: number, sun: number, moon: number, isDayBirth: boolean): number {
  const raw = isDayBirth ? asc + moon - sun : asc + sun - moon;
  return ((raw % 360) + 360) % 360;
}

/**
 * Sect (day vs night) decided geometrically: the Sun is above the horizon when
 * it sits in the 180 degrees of zodiac running from the Descendant to the
 * Ascendant. Using a house number instead makes sect depend on the chosen house
 * system, which flips the Lot of Fortune for births near sunrise or sunset.
 */
export function isDayChart(sunLongitude: number, asc: number): boolean {
  const fromAsc = normalize(sunLongitude - asc);
  return fromAsc >= 180;
}

export interface ComputeChartOptions {
  houseSystem?: HouseSystem;
  includeMinor?: boolean;
  nodeType?: 'mean' | 'true';
}

export function computeChart(
  birthData: BirthData,
  houseSystemOrOpts: HouseSystem | ComputeChartOptions = 'placidus',
  includeMinor = false,
): Chart {
  // Back-compat: older callers pass (birthData, 'placidus', false).
  const opts: ComputeChartOptions = typeof houseSystemOrOpts === 'string'
    ? { houseSystem: houseSystemOrOpts, includeMinor }
    : { houseSystem: 'placidus', includeMinor: false, ...houseSystemOrOpts };
  // BirthData can override the default/options so persisted preference wins.
  const houseSystem = birthData.houseSystem ?? opts.houseSystem ?? 'placidus';
  const useMinor = opts.includeMinor ?? false;
  const nodeType = birthData.nodeType ?? opts.nodeType ?? 'true';
  const unknownTime = !!birthData.unknownTime;

  const { year, month, day, hour, minute, tzOffset, lat, lon } = birthData;
  // When birth time is unknown, default to local noon for planetary positions
  // (the day's mean — Moon may still drift up to ~6° but Sun/inner planets are fine).
  const effHour = unknownTime ? 12 : hour;
  const effMinute = unknownTime ? 0 : minute;
  const utHour = effHour - tzOffset + effMinute / 60;
  const JD = julianDay(year, month, day) + utHour / 24;

  const planets: Record<string, number> = {};
  const retrogrades: Record<string, boolean> = {};
  for (const p of ['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Uranus','Neptune','Pluto','Chiron']) {
    planets[p] = geocentricLongitude(p, JD);
    retrogrades[p] = isRetrograde(p, JD);
  }
  planets.Node = nodeType === 'true' ? lunarNodeTrue(JD) : lunarNode(JD);
  retrogrades.Node = true;
  planets.SouthNode = normalize(planets.Node + 180);
  retrogrades.SouthNode = true;
  planets.Lilith = lilithLongitude(JD);
  retrogrades.Lilith = false;

  const cusps = unknownTime
    ? (new Array(13).fill(null) as unknown as number[])
    : getCusps(houseSystem, JD, lat, lon);
  const { asc, mc } = unknownTime ? { asc: 0, mc: 0 } : ascMC(JD, lat, lon);

  const placements: Record<string, Placement> = {};
  for (const [name, longitude] of Object.entries(planets)) {
    placements[name] = {
      longitude,
      house: unknownTime ? null : houseOf(longitude, cusps),
      retrograde: retrogrades[name],
    };
  }

  // Part of Fortune — only valid when ASC is known.
  if (!unknownTime) {
    const fortuneLon = partOfFortune(asc, planets.Sun, planets.Moon, isDayChart(planets.Sun, asc));
    planets.Fortune = fortuneLon;
    retrogrades.Fortune = false;
    placements.Fortune = {
      longitude: fortuneLon,
      house: houseOf(fortuneLon, cusps),
      retrograde: false,
    };
  }

  // Aspect participants include ASC and MC when birth time is known —
  // these often produce the chart's most personally-felt aspects.
  const aspectPoints: Record<string, number> = { ...planets };
  if (!unknownTime) {
    aspectPoints.Asc = asc;
    aspectPoints.MC = mc;
  }
  const pointNames = Object.keys(aspectPoints);
  const aspects: ChartAspect[] = [];
  for (let i = 0; i < pointNames.length; i++) {
    for (let j = i + 1; j < pointNames.length; j++) {
      const a = aspectBetweenPoints(
        pointNames[i], aspectPoints[pointNames[i]],
        pointNames[j], aspectPoints[pointNames[j]],
        useMinor,
      );
      if (a) aspects.push({ p1: pointNames[i], p2: pointNames[j], ...a });
    }
  }

  const chart: Chart = {
    JD, planets, retrogrades, placements, cusps, asc, mc, aspects, birthData,
    engineVersion: CHART_ENGINE_VERSION,
    unknownTime,
    nodeType,
    houseSystem,
  };
  chart.patterns = computeChartPatterns(chart);
  return chart;
}

export function chartFromPositions(
  positions: Record<string, number>,
  name = 'Imported',
  houseSystem: HouseSystem = 'whole',
): Chart {
  const planets: Record<string, number> = {};
  for (const p of ALL_POINTS) {
    if (positions[p] !== undefined) planets[p] = positions[p];
  }
  const asc = positions.AC !== undefined ? positions.AC : 0;
  const mcDerived = positions.MC === undefined;
  const mc = mcDerived ? normalize(asc + 270) : positions.MC;
  const cusps = new Array(13);
  if (houseSystem === 'equal') {
    for (let h = 1; h <= 12; h++) cusps[h] = normalize(asc + (h - 1) * 30);
  } else {
    // Imported charts without a known birth time default to Whole Sign houses.
    const ascSign = Math.floor(asc / 30);
    for (let h = 1; h <= 12; h++) cusps[h] = normalize((ascSign + (h - 1)) * 30);
  }
  const placements: Record<string, Placement> = {};
  for (const [n, l] of Object.entries(planets)) {
    placements[n] = {
      longitude: l,
      house: houseOf(l, cusps),
      retrograde: false,
      // Pasted/OCR'd charts rarely carry motion, so do not imply "direct".
      retrogradeUnknown: true,
    };
  }
  const names = Object.keys(planets);
  const aspects: ChartAspect[] = [];
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const a = aspectBetweenPoints(names[i], planets[names[i]], names[j], planets[names[j]], false);
      if (a) aspects.push({ p1: names[i], p2: names[j], ...a });
    }
  }
  const chart: Chart = {
    JD: null,
    planets,
    retrogrades: {},
    placements,
    cusps,
    asc,
    mc,
    aspects,
    birthData: {
      name,
      year: 0, month: 0, day: 0, hour: 0, minute: 0,
      tzOffset: 0, lat: 0, lon: 0,
      imported: true,
      houseSystem,
    },
    engineVersion: CHART_ENGINE_VERSION,
    houseSystem,
    mcDerived,
  };
  chart.patterns = computeChartPatterns(chart);
  return chart;
}

export function computeSynastryAspects(chart1: Chart, chart2: Chart, includeMinor: boolean): ChartAspect[] {
  const aspects: ChartAspect[] = [];
  for (const p1 of Object.keys(chart1.planets)) {
    for (const p2 of Object.keys(chart2.planets)) {
      // Cross-chart, so same-name pairs (Sun-Sun) are meaningful; the node axis
      // pair is still definitional within a person's own chart only, so allow it.
      const a = aspectBetweenPoints(p1, chart1.planets[p1], p2, chart2.planets[p2], includeMinor, true);
      if (a) aspects.push({ p1, p2, ...a });
    }
  }
  return aspects;
}

export function balance(planets: Record<string, number>) {
  const el = { Fire: 0, Earth: 0, Air: 0, Water: 0 };
  const mo = { Cardinal: 0, Fixed: 0, Mutable: 0 };
  for (const p of CORE_POINTS) {
    if (planets[p] === undefined) continue;
    const s = Math.floor(planets[p] / 30);
    el[SIGN_ELEMENT[s] as keyof typeof el]++;
    mo[SIGN_MODALITY[s] as keyof typeof mo]++;
  }
  return { el, mo };
}